import React from 'react'
import { useParams } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Spinner from '@divvy-web/skylab.spinner'

import { useGetBankAccountInfo } from '@sputnik/pages/CreditApplicationPages/ConnectBankAccountForm/GetBankAccountInfo.gql'

import ConnectedBankAccount from './ConnectedBankAccount'
import styles from './styles.module.scss'

const ConnectedBankAccounts = () => {
  const [getClassName, makeTestId] = useNameFormatter('ConnectedBankAccounts')
  const { appId } = useParams()

  const { data, loading, error, refetch } = useGetBankAccountInfo({
    variables: {
      creditApplicationId: appId ?? '',
    },
    skip: !appId,
  })

  const connectedBankAccounts =
    data?.creditApplication?.financeInfo?.externalBankOptions?.filter((option) => !!option) || []

  const selectedBankAccountId = data?.creditApplication?.financeInfo?.selectedBankInfo?.id || ''

  if (loading) {
    return (
      <div
        className={styles[getClassName('loading')]}
        data-testid={makeTestId('loading')}
      >
        <Spinner centered />
      </div>
    )
  }

  if (error) {
    return (
      <div
        className={styles[getClassName('error')]}
        data-testid={makeTestId('error')}
      >
        <div className={styles[getClassName('title')]}>
          <FormattedMessage
            defaultMessage='Failed to fetch bank accounts'
            id='sputnik.ConnectedBankAccounts__Jx5k26'
          />
        </div>
        <div>
          <BasicButton
            type={BASIC_BUTTON_TYPE_FLAT}
            onClick={() => refetch({ creditApplicationId: appId ?? '' })}
          >
            <FormattedMessage
              defaultMessage='Reload'
              id='sputnik.ConnectedBankAccounts__fdCS5/'
            />
          </BasicButton>
        </div>
      </div>
    )
  }

  if (!connectedBankAccounts.length) {
    return null
  }

  return (
    <div
      className={styles[getClassName('')]}
      data-testid={makeTestId('')}
    >
      <div className={styles[getClassName('title')]}>
        <FormattedMessage
          defaultMessage='Accounts added automatically'
          id='sputnik.ConnectedBankAccounts__lBHPa7'
        />
      </div>
      <div className={`${styles[getClassName('list')]} fs-mask`}>
        {connectedBankAccounts.map(({ id, bankName, logoUrl, accountNumberLastFour, externalConnectionGuid }) => (
          <ConnectedBankAccount
            key={id}
            externalConnectionGuid={externalConnectionGuid ?? ''}
            id={id ?? ''}
            imageUrl={logoUrl ?? ''}
            isSelected={id === selectedBankAccountId}
            lastFour={accountNumberLastFour ?? ''}
            name={bankName ?? ''}
          />
        ))}
      </div>
    </div>
  )
}

export default ConnectedBankAccounts
